import { PortalTelemetryEvent } from '../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../app/telemetry/portalTelemetryScreens';
import telemetryManifest from '../../app/telemetry/portalTelemetryManifest.json';
import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { useEffect, useState } from "react";
import Typography from '@mui/material/Typography';
import { Button, Paper, Step, StepLabel, Stepper } from '@mui/material';
import EventDetails from './EventDetails';
import OrganiserDetails from './OrganiserDetails';
import Sponsorship from './Sponsorship';
import CalendarOfActivities from './CalendarOfActivities';
import Declaration from './Declaration';
import SupportingDocuments from './SupportingDocuments';
import { useAppDispatch, useAppSelector } from '../../app/store/configureStore';
import { ApiRequestStatus } from '../../app/enums/apiRequestStatus';
import LoadingComponent from '../../app/layout/LoadingComponent';
import { MESSAGE_PROCESSING_DETAILS } from '../../app/utils/constant';
import { AppPathEnum } from "../../app/enums/appPathEnum";
import { gePcsEventByIdAsync } from "../../app/slices/pcsEventSlice";
import PcsEventEducationalDetails from "./PcsEventEducationalDetails";
import EventsInstanceCredits from "./common/EventsInstanceCredits";
import { PcsEventStatusEnum } from "../../app/enums/pcsEventStatusEnum";
import { ApplicantDetails } from "./ApplicantDetails";
import EventsStepsHeader from "./common/EventsStepsHeader";

const eventApprovalSteps = telemetryManifest.features.pcsEventApproval.steps;

export default function EventApprovalSteps() {
    const steps = [
        {
            label: 'Applicant details',
            description: `Applicant details description.`,
        },
        {
            label: 'Event details',
            description: `Event details description.`,
        },
        {
            label: 'Organiser Details',
            description: `Organiser Details description.`,
        },
        {
            label: 'Educational details',
            description: `Educational Aims and Objectives description.`,
        },
        {
            label: 'Sponsorship',
            description: `Sponsorship description.`,
        },
        {
            label: 'Calendar of Activities',
            description: `Calendar of Activities description.`,
        },
        {
            label: 'Supporting Documents',
            description: `Upload Supporting Documents description.`,
        },
        {
            label: 'Declaration',
            description: `Declaration description.`,
        },
    ];

    const [width, setWidth] = useState<number>(window.innerWidth);

    const handleWindowSizeChange = () => {
        setWidth(window.innerWidth);
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        }
    }, []);

    const isMobile = width <= 768;

    const { eventStatuses } = useAppSelector(state => state.ReferenceData);
    const { pcsEventDetails, getPcsEventStatus } = useAppSelector(state => state.pcsEvent);

    const dispatch = useAppDispatch();
    const [isSpaEditabe, setIsSpaEditabe] = useState<boolean>(false);
    const [eventId,] = useState(new URLSearchParams(window.location.search).get("eventId"));
    const [cloneEventId,] = useState(new URLSearchParams(window.location.search).get("cloneEventId"));

    useEffect(() => {
        if (eventId && eventId !== null && eventId !== "") {
            dispatch(gePcsEventByIdAsync(eventId));
        }
        else if (cloneEventId && cloneEventId !== null && cloneEventId !== "") {
            dispatch(gePcsEventByIdAsync(cloneEventId));
        }
    }, [cloneEventId, dispatch, eventId]);

    useEffect(() => {
        if (pcsEventDetails !== null && eventStatuses && eventStatuses?.length > 0 && eventId && eventId !== null && eventId !== "") {
            setIsSpaEditabe(!(eventStatuses?.find(x => x.id === pcsEventDetails.eventStatusId)?.userEditable ?? false));
        }
    }, [pcsEventDetails, eventStatuses, eventId]);

    function getStepContent(step: number) {
        switch (step) {
            case 0:
                return <ApplicantDetails handleNext={handleNext} />
            case 1:
                return <EventDetails handleNext={handleNext} isSpaEditabe={isSpaEditabe} handleBack={handleBack} />
            case 2:
                return <OrganiserDetails handleNext={handleNext} handleBack={handleBack} isSpaEditabe={isSpaEditabe} />
            case 3:
                return <PcsEventEducationalDetails handleNext={handleNext} handleBack={handleBack} isSpaEditabe={isSpaEditabe} />
            case 4:
                return <Sponsorship handleNext={handleNext} handleBack={handleBack} isSpaEditabe={isSpaEditabe} />
            case 5:
                return <CalendarOfActivities handleNext={handleNext} handleBack={handleBack} isSpaEditabe={isSpaEditabe} />
            case 6:
                return <SupportingDocuments handleNext={handleNext} handleBack={handleBack} isSpaEditabe={isSpaEditabe} />
            case 7:
                return <Declaration handleBack={handleBack} isSpaEditabe={isSpaEditabe} />
            default:
                throw new Error('Unknown step');
        }
    }

    const [activeStep, setActiveStep] = useState(0);
    usePortalScreen(portalScreenContext(PortalTelemetryScreen.EventApprovalSteps), eventApprovalSteps[activeStep],
        getPcsEventStatus !== ApiRequestStatus.Pending, eventId ? 'existing' : cloneEventId ? 'clone' : 'new');

    const handleNext = () => {
        trackPortalEvent(portalScreenContext(PortalTelemetryScreen.EventApprovalSteps), PortalTelemetryEvent.ActionSelected, { action: 'next', step: eventApprovalSteps[activeStep], direction: 'next' });
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        trackPortalEvent(portalScreenContext(PortalTelemetryScreen.EventApprovalSteps), PortalTelemetryEvent.ActionSelected, { action: 'back', step: eventApprovalSteps[activeStep], direction: 'back' });
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const redirectToPcsEventDashboard = () => {
        trackPortalEvent(portalScreenContext(PortalTelemetryScreen.EventApprovalSteps), PortalTelemetryEvent.ActionSelected, { action: 'dashboard', step: eventApprovalSteps[activeStep], direction: 'dashboard' });
        let url = new URLSearchParams(window.location.search);
        url.delete('eventId');
        url.delete('cloneEventId');
        url.toString();
        window.location.href = `${AppPathEnum.PCS_EVENT_APPROVAL}?${url.toString()}`;
    }

    if (getPcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <>
        <EventsStepsHeader />
        <br></br>
        <Button variant="contained" onClick={redirectToPcsEventDashboard}>
            Back to PCS events dashboard
        </Button>
        <br></br><br></br>

        {eventStatuses?.find(x => x.id === pcsEventDetails?.eventStatusId)?.code === PcsEventStatusEnum.ApplicationApproved &&
            (cloneEventId === '' || cloneEventId === null || cloneEventId === undefined) && <EventsInstanceCredits />}

        <Stepper activeStep={activeStep} sx={{ mt: '2%', mb: '2%' }} orientation={isMobile === true ? "vertical" : "horizontal"}>
            {steps.map((step) => (
                <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                </Step>
            ))}
        </Stepper>

        {getStepContent(activeStep)}

        {activeStep === steps.length && (
            <Paper square elevation={0} sx={{ p: 3 }}>
                <Typography>All steps completed - you&apos;re finished</Typography>
                <Button onClick={portalAction(portalScreenContext(PortalTelemetryScreen.EventApprovalSteps), `${PortalTelemetryScreen.EventApprovalSteps}.onClick.handleReset`, handleReset)} sx={{ mt: 1, mr: 1 }}>
                    Reset
                </Button>
            </Paper>
        )}
    </>
}
